import { FC, useEffect, useRef, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { Datatable } from '../../../components/Datatable/Datatable'
import type { MetaType } from '../../../components/Datatable/Datatable'
import { Button } from '../../../components/Button/Button'
import useModals from '../../../hooks/useModals'
import { DELETE, GET, POST } from '../../../services/api'
import { Loading } from '../../../components/Loading/Loading'
import { ModalDelete } from '../../../components/Modals/General/ModalDelete/ModalDelete'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { i18n } from '../../../translate/i18n'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { useSwitchStatus } from '../../../components/Datatable/SwitchStatus'
import {
  RoomStatus,
  RoomType,
  HotelRoomType,
 
 
  getStatusColor,
  getStatusLabel,
  getTypeLabel
} from './types'
import { Input } from '../../../components/Input/Input'
import { getReservationStatusLabel, ReservationRoomStatus } from '../HotelRoomReservation/types'

const HotelRoomContent: FC = () => {
  const navigate = useNavigate()
  const { showDeleteModal } = useModals()
  const [loading, setLoading] = useState(false)
  const { switchStatus } = useSwitchStatus()
  const [hotelRooms, setHotelRooms] = useState<HotelRoomType[]>([])
  const [id, setId] = useState<number | null>(null)
  const [meta, setMeta] = useState<MetaType | undefined>(undefined)
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false)
  const [bulkCreateData, setBulkCreateData] = useState({
    startNumber: '',
    endNumber: '',
    capacity: '',
    price: '',
    type: RoomType.SINGLE,
    prefix: ''
  })
  const [filterRooms, setFilterRooms] = useState({
    status: '',
    type: ''
  })
  const [roomTypes, setRoomTypes] = useState<{ value: string, select: string }[]>([
    {
      value: 'single',
      select: 'Single'
    },
    {
      value: 'double',
      select: 'Double'
    },
    {
      value: 'twin',
      select: 'Twin'
    },
    {
      value: 'queen',
      select: 'Queen'
    },
    {
      value: 'king',
      select: 'King'
    },
    {
      value: 'suite',
      select: 'Suite'
    },
    {
      value: 'junior_suite',
      select: 'Junior Suite'
    },
    {
      value: 'master_suite',
      select: 'Master Suite'
    },
    {
      value: 'presidential',
      select: 'Presidential'
    },
    {
      value: 'family',
      select: 'Family'
    },
    {
      value: 'accessible',
      select: 'Accessible'
    }
  ])

  const handleFilterChange = (field: keyof typeof filterRooms, value: any) => {
    setFilterRooms(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const getHotelRooms = async () => {
    try {
      setLoading(true)
      const res = await POST('/hotel-room/filter', filterRooms)
      const formattedRooms = res.data.map((room: any) => ({
        id: room.id,
        roomNumber: room.roomNumber,
        status: room.status,
        capacity: room.capacity,
        price: room.price,
        type: room.type,
        pluginspaceId: room.pluginspaceId,
        statusLabel: getStatusLabel(room.status),
        typeLabel: room.type ? getTypeLabel(room.type) : '-',
        priceFormatted: room.price ? `R$ ${room.price.toFixed(2)}` : '-',
        capacityLabel: room.capacity ? `${room.capacity} pessoas` : '-',
      }))
      setHotelRooms(formattedRooms)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const deleteHotelRoom = async () => {
    try {
      setLoading(true)
      const gridRes = await DELETE(`/hotel-room/${id}`)
      if (gridRes.success === true) {
        Swal.fire({
          icon: 'success',
          title: 'Quarto deletado',
          text: 'Deleção realizada com sucesso!',
        }).then(async () => await getHotelRooms())
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao deletar o quarto!',
        }).then(async () => await getHotelRooms())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao deletar o quarto!',
      }).then(async () => await getHotelRooms())
      console.log(e)
    }
  }

  const createBulkRooms = async () => {
    try {
      if (!bulkCreateData.startNumber || !bulkCreateData.endNumber || !bulkCreateData.capacity || !bulkCreateData.price) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Número inicial e final, capacidade e preço são obrigatórios!',
        })
        return
      }

      const start = parseInt(bulkCreateData.startNumber)
      const end = parseInt(bulkCreateData.endNumber)

      if (start > end) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Número inicial deve ser menor que o final!',
        })
        return
      }

      setLoading(true)
      const roomsToCreate = []

      for (let i = start; i <= end; i++) {
        const roomNumber = bulkCreateData.prefix ? `${bulkCreateData.prefix}${i}` : i.toString()
        roomsToCreate.push({
          roomNumber,
          capacity: bulkCreateData.capacity ? parseInt(bulkCreateData.capacity) : undefined,
          price: bulkCreateData.price ? parseFloat(bulkCreateData.price) : undefined,
          type: bulkCreateData.type,
          status: RoomStatus.AVAILABLE,
        })
      }

      await POST('/hotel-room/bulk-creation', roomsToCreate)

      Swal.fire({
        icon: 'success',
        title: 'Quartos criados',
        text: `${roomsToCreate.length} quartos criados com sucesso!`,
      }).then(async () => {
        await getHotelRooms()
        setShowBulkCreateModal(false)
        setBulkCreateData({
          startNumber: '',
          endNumber: '',
          capacity: '',
          price: '',
          type: RoomType.SINGLE,
          prefix: ''
        })
      })

      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao criar os quartos!',
      })
      console.log(e)
    }
  }

  useEffect(() => {
    getHotelRooms()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_hotel_room.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_hotel_room.subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <div className='d-flex gap-2'>
                  <Button
                    text={i18n.t('hospitality_hotel_room.create_multiples')}
                    color='warning'
                    iconColor='#FFF'
                    icon='fas fa-layer-group'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={() => setShowBulkCreateModal(true)}
                    btnClass='mb-5 fw-bolder'
                  />
                  <Button
                    text={i18n.t('hospitality_hotel_room.add_room')}
                    color='primary'
                    iconColor='#FFF'
                    icon='fas fa-plus'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={() => navigate('criar')}
                    btnClass='mb-5 fw-bolder'
                  />
                </div>
              </div>
            </div>
            <div className='card-body border-bottom'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <Input
                    inputAttr={{ type: 'select' }}
                    label={i18n.t('hospitality_room_reservation.filter_status_label')}
                    value={filterRooms.status}
                    change={(value: string) => handleFilterChange('status', value)}
                    options={[
                      { value: '', select: i18n.t('hospitality_room_reservation.filter_status_all') },
                      ...Object.values(RoomStatus).map((status) => ({
                        value: status,
                        select: getStatusLabel(status),
                      })),
                    ]}
                    placeholder={i18n.t('hospitality_room_reservation.filter_status_placeholder')}
                  />
                </div>

                <div className='col-md-3'>
                  <Input
                    inputAttr={{ type: 'select' }}
                    label='Tipo de Quarto'
                    value={filterRooms.type}
                    change={(value: string) => handleFilterChange('type', value)}
                    options={[
                      { value: '', select: 'Todos os tipos' },
                      ...roomTypes
                    ]}
                    placeholder='Selecionar tipo'
                  />
                </div>


                <div className='col-md-6  justify-content-end align-items-center'>
                  <div className='d-flex w-100 h-100 justify-content-end align-items-end'>
                    <Button
                      text='Buscar'
                      color='primary'
                      size='small'
                      click={getHotelRooms}
                    />
                  </div>

                </div>
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={hotelRooms}
                headers={[
                  { key: i18n.t('hospitality_hotel_room.header_number'), value: 'roomNumber', type: 'default' },
                  { key: i18n.t('hospitality_hotel_room.header_status'), value: 'statusLabel', type: 'default' },
                  { key: i18n.t('hospitality_hotel_room.header_type'), value: 'typeLabel', type: 'default' },
                  { key: i18n.t('hospitality_hotel_room.header_capacity'), value: 'capacityLabel', type: 'default' },
                  { key: i18n.t('hospitality_hotel_room.header_price'), value: 'priceFormatted', type: 'default' },
                  { key: i18n.t('hospitality_hotel_room.header_actions'), value: 'acoes', type: '' },
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      navigate(`editar/${item.id}`)
                    },
                    title: i18n.t('hospitality_hotel_room.action_edit'),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(i18n.t('hospitality_hotel_room.action_delete'))
                      setId(item.id)
                    },
                    title: i18n.t('hospitality_hotel_room.action_delete'),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteHotelRoom()} />

          {/* Modal para criar múltiplos quartos */}
          {showBulkCreateModal && (
            <div className='modal fade show d-block' tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className='modal-dialog modal-dialog-centered'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title'>{i18n.t('hospitality_hotel_room.modal_bulk_title')}</h5>
                    <button
                      type='button'
                      className='btn-close'
                      onClick={() => setShowBulkCreateModal(false)}
                    ></button>
                  </div>
                  <div className='modal-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='mb-3'>
                          <label className='form-label'>{i18n.t('hospitality_hotel_room.modal_bulk_prefix')}</label>
                          <input
                            type='text'
                            className='form-control'
                            value={bulkCreateData.prefix}
                            onChange={(e) => setBulkCreateData({ ...bulkCreateData, prefix: e.target.value })}
                            placeholder={i18n.t('hospitality_hotel_room.modal_bulk_prefix_placeholder')}
                          />
                        </div>
                      </div>
                      <div className='col-md-3'>
                        <div className='mb-3'>
                          <label className='form-label'>{i18n.t('hospitality_hotel_room.modal_bulk_start')}</label>
                          <input
                            type='number'
                            className='form-control'
                            value={bulkCreateData.startNumber}
                            onChange={(e) => setBulkCreateData({ ...bulkCreateData, startNumber: e.target.value })}
                            placeholder={i18n.t('hospitality_hotel_room.modal_bulk_start_placeholder')}
                          />
                        </div>
                      </div>
                      <div className='col-md-3'>
                        <div className='mb-3'>
                          <label className='form-label'>{i18n.t('hospitality_hotel_room.modal_bulk_end')}</label>
                          <input
                            type='number'
                            className='form-control'
                            value={bulkCreateData.endNumber}
                            onChange={(e) => setBulkCreateData({ ...bulkCreateData, endNumber: e.target.value })}
                            placeholder={i18n.t('hospitality_hotel_room.modal_bulk_end_placeholder')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label className='form-label'>{i18n.t('hospitality_hotel_room.modal_bulk_capacity')}</label>
                          <input
                            type='number'
                            className='form-control'
                            value={bulkCreateData.capacity}
                            onChange={(e) => setBulkCreateData({ ...bulkCreateData, capacity: e.target.value })}
                            placeholder={i18n.t('hospitality_hotel_room.modal_bulk_capacity_placeholder')}
                          />
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label className='form-label'>{i18n.t('hospitality_hotel_room.modal_bulk_price')}</label>
                          <input
                            type='number'
                            step='0.01'
                            className='form-control'
                            value={bulkCreateData.price}
                            onChange={(e) => setBulkCreateData({ ...bulkCreateData, price: e.target.value })}
                            placeholder={i18n.t('hospitality_hotel_room.modal_bulk_price_placeholder')}
                          />
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='mb-3'>
                          <label className='form-label'>{i18n.t('hospitality_hotel_room.modal_bulk_type')}</label>
                          <select
                            className='form-select'
                            value={bulkCreateData.type}
                            onChange={(e) => setBulkCreateData({ ...bulkCreateData, type: e.target.value as RoomType })}
                          >
                            <option value={RoomType.SINGLE}>{i18n.t('hospitality_hotel_room.type_single')}</option>
                            <option value={RoomType.DOUBLE}>{i18n.t('hospitality_hotel_room.type_double')}</option>
                            <option value={RoomType.TWIN}>{i18n.t('hospitality_hotel_room.type_twin')}</option>
                            <option value={RoomType.QUEEN}>{i18n.t('hospitality_hotel_room.type_queen')}</option>
                            <option value={RoomType.KING}>{i18n.t('hospitality_hotel_room.type_king')}</option>
                            <option value={RoomType.SUITE}>{i18n.t('hospitality_hotel_room.type_suite')}</option>
                            <option value={RoomType.JUNIOR_SUITE}>{i18n.t('hospitality_hotel_room.type_junior_suite')}</option>
                            <option value={RoomType.MASTER_SUITE}>{i18n.t('hospitality_hotel_room.type_master_suite')}</option>
                            <option value={RoomType.PRESIDENTIAL}>{i18n.t('hospitality_hotel_room.type_presidential')}</option>
                            <option value={RoomType.FAMILY}>{i18n.t('hospitality_hotel_room.type_family')}</option>
                            <option value={RoomType.ACCESSIBLE}>{i18n.t('hospitality_hotel_room.type_accessible')}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button
                      type='button'
                      className='btn btn-sm btn-secondary'
                      onClick={() => setShowBulkCreateModal(false)}
                    >
                      {i18n.t('hospitality_hotel_room.modal_bulk_cancel')}
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      onClick={createBulkRooms}
                    >
                      {i18n.t('hospitality_hotel_room.modal_bulk_create')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

const HotelRoom: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: i18n.t('hospitality_hotel_room.breadcrumb_dashboard'), path: 'dashboard', isActive: true },
          { title: i18n.t('hospitality_hotel_room.breadcrumb_hotel_room'), path: 'hotel-room', isActive: true },
        ]}
      >
        {i18n.t('hospitality_hotel_room.title')}
      </PageTitle>
      <HotelRoomContent />
    </>
  )
}

export { HotelRoom }